import { z } from 'zod'

import { bsDateOrDashSchema, moneySchema } from '@/schemas/common'
import { tablePageSchema, tableQuerySchema } from '@/schemas/table'

/**
 * Market depth — the five best bids and asks per company.
 *
 * A book is not a price table row: it is a small table of its own, and the
 * page draws six of them in a grid. It still pages like a table, so the
 * envelope is the shared `TablePage` and one "row" is one whole book.
 *
 * Prices are `Money` and quantities are counts. The distinction matters more
 * here than anywhere else on the Market pages: a ladder is read down the price
 * column and across to the kitta column, and a rupee that had passed through a
 * float would show a rung at 497.00000000001.
 */

/**
 * The rungs a ladder draws per side — the design's five best.
 *
 * A ceiling, never a count: mm-be passes the upstream book through unchanged
 * (services/market/depthclient.py `_parse`) and promises no length, and its own
 * `_message` has a branch for a book with no orders at all. A thin book has
 * fewer rungs, and `DepthBookCard` prints '—' for the missing ones.
 */
export const DEPTH_LEVELS = 5

/** One rung: a price and the kitta resting on it. */
export const depthLevelSchema = z.object({
  price: moneySchema,
  kitta: z.number().int().nonnegative(),
})

/** The line under each side: "6,890 kitta · 19 orders". */
export const depthTotalSchema = z.object({
  kitta: z.number().int().nonnegative(),
  orders: z.number().int().nonnegative(),
})

export const depthBookSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** `null` when this symbol is out of the live snapshot and its own book
   *  carries no bids or asks to infer a price from. */
  ltp: moneySchema.nullable(),
  percentChange: moneySchema.nullable(),
  /** Best bid first, so the ladder reads down away from the touch. */
  bids: z.array(depthLevelSchema).max(DEPTH_LEVELS),
  /** Best ask first, likewise — the two columns meet at the spread. */
  asks: z.array(depthLevelSchema).max(DEPTH_LEVELS),
  bidTotal: depthTotalSchema,
  askTotal: depthTotalSchema,
  /**
   * Already worded: "Supply is 1.18 times demand". The sentence names which
   * side is heavier, which a bare ratio cannot do without the reader
   * remembering which way round the division went.
   */
  ratioCaption: z.string().min(1),
})

/**
 * `symbols` is a list, not one name: the page's second control is a
 * multi-select and the grid draws a book per chosen company. An empty list
 * means the whole sector, which is what the screen shows before anyone has
 * narrowed it.
 */
export const depthQuerySchema = tableQuerySchema.extend({
  sector: z.string().min(1),
  symbols: z.array(z.string().min(1)),
})

export const depthPageSchema = tablePageSchema(depthBookSchema).extend({
  intro: z.string().min(1),
  footnote: z.string().min(1),
  asOf: bsDateOrDashSchema,
  mitraSays: z.string().min(1),
})

/**
 * One option in the sector select.
 *
 * Its names are fetched on their own (`depthCompanySchema`), per sector: mm-be
 * only lists a sector's members one sector at a time, and three of the four
 * screens that draw this select never offer the names at all.
 */
export const depthSectorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
})

/** One name the company picker offers inside a sector. */
export const depthCompanySchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
})

export type DepthLevel = z.infer<typeof depthLevelSchema>
export type DepthTotal = z.infer<typeof depthTotalSchema>
export type DepthBook = z.infer<typeof depthBookSchema>
export type DepthQuery = z.infer<typeof depthQuerySchema>
export type DepthPage = z.infer<typeof depthPageSchema>
export type DepthSector = z.infer<typeof depthSectorSchema>
export type DepthCompany = z.infer<typeof depthCompanySchema>
